import React, { useEffect, useState } from "react";
import { Icon } from "./icons";
import "./ManageNotices.css";

const API =
  process.env.REACT_APP_API_URL ||
  "https://backend-9i6n.onrender.com";

const ManageNotices = () => {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [isImportant, setIsImportant] = useState(false);
  const [file, setFile] = useState(null);

  const [notices, setNotices] = useState([]);
  const [search, setSearch] = useState("");
  const [onlyImportant, setOnlyImportant] = useState(false);

  const [editPopup, setEditPopup] = useState(false);
  const [currentEdit, setCurrentEdit] = useState(null);
  const [editTitle, setEditTitle] = useState("");
  const [editDescription, setEditDescription] = useState("");
  const [editFile, setEditFile] = useState(null);

  // Loading states
  const [isAdding, setIsAdding] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isDeleting, setIsDeleting] = useState(null);

  // Popup message state
  const [popup, setPopup] = useState({ show: false, message: "", type: "" });

  const token = localStorage.getItem("token");

  /* ================= SHOW POPUP ================= */
  const showPopup = (message, type = "success") => {
    setPopup({ show: true, message, type });
    setTimeout(() => {
      setPopup({ show: false, message: "", type: "" });
    }, 1800);
  };

  /* ================= FETCH ================= */
  const fetchNotices = async () => {
    try {
      const res = await fetch(`${API}/api/notices`);
      const data = await res.json();
      setNotices(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Fetch notice error:", err);
      showPopup("सूचना लोड करता आल्या नाहीत", "error");
    }
  };

  useEffect(() => {
    fetchNotices();
  }, []);

  /* ================= ADD ================= */
  const addNotice = async (e) => {
    e.preventDefault();
    setIsAdding(true);

    try {
      const formData = new FormData();
      formData.append("title", title);
      formData.append("description", description);
      formData.append("isImportant", isImportant);
      if (file) formData.append("attachment", file);

      const res = await fetch(`${API}/api/notices`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      if (res.ok) {
        setTitle("");
        setDescription("");
        setIsImportant(false);
        setFile(null);

        const fileInput = document.querySelector('input[type="file"]');
        if (fileInput) fileInput.value = "";

        showPopup("सूचना यशस्वीरित्या जोडली!", "success");
        fetchNotices();
      } else {
        showPopup("सूचना जोडता आली नाही", "error");
      }
    } catch (err) {
      console.error("Add notice error:", err);
      showPopup("सूचना जोडताना त्रुटी आली", "error");
    } finally {
      setIsAdding(false);
    }
  };

  /* ================= DELETE ================= */
  const deleteNotice = async (id) => {
    if (!window.confirm("ही सूचना नक्की हटवायची आहे का?")) return;

    setIsDeleting(id);

    try {
      const res = await fetch(`${API}/api/notices/${id}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (res.ok) {
        showPopup("सूचना यशस्वीरित्या हटवली", "success");
        fetchNotices();
      } else {
        showPopup("सूचना हटवता आली नाही", "error");
      }
    } catch (err) {
      console.error("Delete notice error:", err);
      showPopup("हटवताना त्रुटी आली", "error");
    } finally {
      setIsDeleting(null);
    }
  };

  /* ================= OPEN EDIT ================= */
  const openEditPopup = (notice) => {
    setCurrentEdit(notice);
    setEditTitle(notice.title);
    setEditDescription(notice.description);
    setEditFile(null);
    setEditPopup(true);
  };

  /* ================= UPDATE ================= */
  const updateNotice = async () => {
    setIsUpdating(true);

    try {
      const formData = new FormData();
      formData.append("title", editTitle);
      formData.append("description", editDescription);
      if (editFile) formData.append("attachment", editFile);

      const res = await fetch(`${API}/api/notices/${currentEdit._id}`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      if (res.ok) {
        setEditPopup(false);
        setCurrentEdit(null);
        showPopup("सूचना यशस्वीरित्या अद्ययावत केली!", "success");
        fetchNotices();
      } else {
        showPopup("सूचना अद्ययावत करता आली नाही", "error");
      }
    } catch (err) {
      console.error("Update notice error:", err);
      showPopup("अद्ययावत करताना त्रुटी आली", "error");
    } finally {
      setIsUpdating(false);
    }
  };

  /* ================= ATTACHMENT URL ================= */
  const getAttachmentUrl = (attachment) => {
    if (!attachment) return null;
    return attachment.startsWith("http")
      ? attachment
      : `${API}${attachment}`;
  };

  /* ================= FILTER (client-side, no logic change to API) ================= */
  const shown = notices
    .filter((n) => (onlyImportant ? n.isImportant : true))
    .filter((n) => {
      if (!search.trim()) return true;
      const q = search.trim().toLowerCase();
      return (
        n.title?.toLowerCase().includes(q) ||
        n.description?.toLowerCase().includes(q)
      );
    });

  return (
    <div className="admin-page">
      {/* TOAST */}
      {popup.show && (
        <div className={`gp-toast gp-toast-${popup.type === "error" ? "error" : "success"}`}>
          <span>{popup.type === "success" ? "✅" : "❌"}</span>
          <p style={{ margin: 0 }}>{popup.message}</p>
        </div>
      )}

      {/* HEADER */}
      <div className="gp-page-head">
        <div>
          <h1>सूचना व्यवस्थापन</h1>
          <p>सर्व सार्वजनिक सूचना तयार करा, संपादित करा व व्यवस्थापित करा</p>
        </div>
      </div>

      {/* ADD FORM */}
      <div className="gp-card notice-form-card">
        <h3 className="notice-form-title">नवीन सूचना जोडा</h3>
        <form className="notice-form-grid" onSubmit={addNotice}>
          <label className="gp-field">
            सूचना शीर्षक *
            <input
              className="gp-input"
              type="text"
              placeholder="सूचनेचे शीर्षक टाका"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
              disabled={isAdding}
            />
          </label>

          <label className="gp-field">
            तपशील *
            <textarea
              className="gp-textarea"
              placeholder="सविस्तर तपशील टाका"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              required
              disabled={isAdding}
            />
          </label>

          <label className="gp-checkbox-row">
            <input
              type="checkbox"
              checked={isImportant}
              onChange={(e) => setIsImportant(e.target.checked)}
              disabled={isAdding}
            />
            महत्त्वाची सूचना म्हणून चिन्हांकित करा
          </label>

          <button type="submit" className="gp-btn gp-btn-green" disabled={isAdding}>
            {isAdding ? (
              <>
                <span className="gp-spinner"></span> जोडत आहे...
              </>
            ) : (
              <>
                <Icon.plus size={16} /> सूचना जोडा
              </>
            )}
          </button>
        </form>
      </div>

      {/* TOOLBAR */}
      <div className="gp-toolbar" style={{ marginTop: 26 }}>
        <div className="gp-search-box">
          <Icon.search size={16} />
          <input
            placeholder="सूचना शोधा…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="gp-chip-group">
          <button
            className={`gp-chip ${!onlyImportant ? "active" : ""}`}
            onClick={() => setOnlyImportant(false)}
          >
            सर्व
          </button>
          <button
            className={`gp-chip ${onlyImportant ? "active" : ""}`}
            onClick={() => setOnlyImportant(true)}
          >
            महत्त्वाच्या
          </button>
        </div>
        <span className="gp-toolbar-count">एकूण {shown.length} नोंदी</span>
      </div>

      {/* NOTICE LIST */}
      {notices.length === 0 ? (
        <div className="gp-empty">कोणतीही सूचना आढळली नाही. वरील फॉर्ममधून पहिली सूचना तयार करा!</div>
      ) : shown.length === 0 ? (
        <div className="gp-empty">या शोधासाठी कोणतीही सूचना सापडली नाही</div>
      ) : (
        <div className="gp-record-grid">
          {shown.map((n) => (
            <div key={n._id} className="gp-record-card">
              <div className="gp-record-top">
                {n.isImportant ? (
                  <span className="gp-badge gp-badge-amber">
                    <span className="gp-badge-dot" /> महत्त्वाची
                  </span>
                ) : (
                  <span className="gp-badge gp-badge-neutral">सर्वसाधारण</span>
                )}
              </div>

              <h3 className="gp-record-title">{n.title}</h3>
              <p className="gp-record-desc">{n.description}</p>

              {n.attachment && (
                <a
                  href={getAttachmentUrl(n.attachment)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="gp-attachment-link"
                >
                  <Icon.attachment size={13} /> संलग्नक पहा
                </a>
              )}

              <div className="gp-record-foot">
                <span className="gp-record-date">
                  {n.createdAt && (
                    <>
                      <Icon.calendar size={14} />
                      {new Date(n.createdAt).toLocaleDateString("en-GB")}
                    </>
                  )}
                </span>
                <div className="gp-record-actions">
                  <button
                    className="gp-icon-btn"
                    title="संपादित करा"
                    onClick={() => openEditPopup(n)}
                    disabled={isDeleting === n._id}
                  >
                    <Icon.edit size={15} />
                  </button>
                  <button
                    className="gp-icon-btn gp-icon-btn-danger"
                    title="हटवा"
                    onClick={() => deleteNotice(n._id)}
                    disabled={isDeleting === n._id}
                  >
                    {isDeleting === n._id ? (
                      <span className="gp-spinner gp-spinner-dark" />
                    ) : (
                      <Icon.trash size={15} />
                    )}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* EDIT MODAL */}
      {editPopup && (
        <div
          className="gp-modal-overlay"
          onClick={() => !isUpdating && setEditPopup(false)}
        >
          <div className="gp-modal" onClick={(e) => e.stopPropagation()}>
            <div className="gp-modal-head">
              <h3>सूचना संपादित करा</h3>
              <button
                className="gp-modal-close"
                onClick={() => setEditPopup(false)}
                disabled={isUpdating}
              >
                <Icon.x size={15} />
              </button>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              <label className="gp-field">
                शीर्षक
                <input
                  className="gp-input"
                  type="text"
                  value={editTitle}
                  onChange={(e) => setEditTitle(e.target.value)}
                  disabled={isUpdating}
                />
              </label>

              <label className="gp-field">
                तपशील
                <textarea
                  className="gp-textarea"
                  value={editDescription}
                  onChange={(e) => setEditDescription(e.target.value)}
                  disabled={isUpdating}
                />
              </label>

              {currentEdit?.attachment && (
                <a
                  href={getAttachmentUrl(currentEdit.attachment)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="gp-attachment-link"
                >
                  <Icon.attachment size={13} /> सध्याची फाईल पहा
                </a>
              )}

              <label className="gp-field">
                नवीन संलग्नक (पर्यायी)
                <input
                  className="gp-input"
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  onChange={(e) => setEditFile(e.target.files[0])}
                  disabled={isUpdating}
                  style={{ paddingTop: 8 }}
                />
              </label>
            </div>

            <div className="gp-modal-actions">
              <button
                onClick={updateNotice}
                className="gp-btn gp-btn-blue"
                disabled={isUpdating}
              >
                {isUpdating ? (
                  <>
                    <span className="gp-spinner" /> जतन करत आहे...
                  </>
                ) : (
                  "बदल जतन करा"
                )}
              </button>
              <button
                onClick={() => setEditPopup(false)}
                className="gp-btn gp-btn-ghost"
                disabled={isUpdating}
              >
                रद्द करा
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageNotices;
